import React, { useState, useEffect, useRef, useCallback } from "react";
import { useGlobalLoader } from "../../context/GlobalLoaderContext";
import { addResult, deleteResult, approveResult, getJobs } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

function HrResultAdminPanel({ darkMode }) {
  const { loading: globalLoading } = useGlobalLoader();
  const { threeTable, fetchThreeTableSection } = useGlobalData();
  const [results, setResults] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [resultsOffset, setResultsOffset] = useState(0);
  const [resultsHasMore, setResultsHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  // Remove local isLoading, use global loader
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const [formData, setFormData] = useState({ jobSlug: "" });
  const role = localStorage.getItem("role");

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      await fetchThreeTableSection("results", false, { limit: PAGE_LIMIT, offset: 0 });
      const jobsRes = await getJobs();
      if (!ignore) setJobs(jobsRes.data);
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line
  }, []);

  // Sync results from global state
  useEffect(() => {
    setResults((threeTable.results || []).slice());
    setResultsOffset(threeTable.results?.length || 0);
    setResultsHasMore((threeTable.results?.length || 0) === PAGE_LIMIT);
  }, [threeTable.results]);

  // Infinite scroll fetch
  const fetchMoreResults = useCallback(async () => {
    if (!resultsHasMore || isFetchingMore) return;
    setIsFetchingMore(true);
    await fetchThreeTableSection("results", false, { limit: PAGE_LIMIT, offset: resultsOffset });
    setIsFetchingMore(false);
  }, [resultsHasMore, isFetchingMore, resultsOffset, fetchThreeTableSection]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!resultsHasMore || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreResults();
    }
  }, [resultsHasMore, isFetchingMore, fetchMoreResults]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleApprove = async (id) => {
    await approveResult(id);
    await fetchThreeTableSection("results", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addResult(formData);
    setFormData({ jobSlug: "" });
    await fetchThreeTableSection("results", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this result?")) {
      await deleteResult(id);
      await fetchThreeTableSection("results", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  const getJobTitle = (jobSlug) => jobs.find((job) => job.slug === jobSlug)?.title || jobSlug;
  const filteredResults = results.filter((result) => !formData.jobSlug || result.jobSlug === formData.jobSlug);

  if (role !== "hr") {
    return <div className="admin-panel-error">❌ Access denied! Only HR can access this panel.</div>;
  }

  return (
    <div className={`resource-admin-panel resource-admin-panel--red${darkMode ? " dark-mode" : ""}`}> 
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">HR Result Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Yahan se result entries ko manage karo. Job choose karke result add karo, filtered list me dekho aur live page ko open ya delete karo.</p>
        </div>
        <div className="resource-admin-panel__pill">Results</div>
      </div>

      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label>Job:</label>
          <select
            value={formData.jobSlug}
            onChange={(e) => setFormData({ ...formData, jobSlug: e.target.value })}
            required
          >
            <option value="">Select Job</option>
            {jobs.map((job) => (
              <option key={job.slug} value={job.slug}>
                {job.title}
              </option>
            ))}
          </select>
        </div>
        <button type="submit">Add Result</button>
      </form>

      <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
        <h3>Result Entries</h3>
        {globalLoading ? (
          <div className="global-loader"><div className="skeleton-loader" /></div>
        ) : (
        <ul>
          {filteredResults.map((result) => {
            const isPending = result.status === "pending";
            const statusColor = isPending ? "#f7b500" : "#2ecc40";
            return (
              <li key={result._id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span>{getJobTitle(result.jobSlug)}</span>
                <span style={{
                  background: statusColor,
                  color: "#fff",
                  borderRadius: 6,
                  padding: "2px 10px",
                  fontWeight: 600,
                  fontSize: 13
                }}>{result.status === "pending" ? "Pending" : "Approved"}</span>
                {isPending && (
                  <>
                    <button onClick={() => handleApprove(result._id)} style={{ background: "#f7b500", color: "#fff", border: "none", borderRadius: 4, padding: "2px 10px", marginLeft: 8 }}>Approve</button>
                    <button onClick={() => handleDelete(result._id)} style={{ marginLeft: 8 }}>Delete</button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        )}
        {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
        {!resultsHasMore && !isFetchingMore && results.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more results.</div>}
      </div>
    </div>
  );
}

export default HrResultAdminPanel;
